import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Save,
  Loader2,
  Trash2,
  Plus,
  BookOpen,
  ClipboardList,
  MessageSquare,
  ChevronUp,
  ChevronDown,
  X,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrackDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [track, setTrack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", status: "draft", submission_type: "generic", custom_submission_message: "" });
  const [pickerType, setPickerType] = useState(null); // "lexp" or "assessment"
  const [library, setLibrary] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetchTrack = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tracks/${id}`, { withCredentials: true });
      setTrack(data);
      setForm({
        title: data.title || "",
        description: data.description || "",
        status: data.status || "draft",
        submission_type: data.submission_type || "generic",
        custom_submission_message: data.custom_submission_message || "",
      });
    } catch {
      toast.error("Failed to load track");
      navigate("/admin/tracks");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchTrack(); }, [fetchTrack]);

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/tracks/${id}`, form, { withCredentials: true });
      toast.success("Track updated");
      fetchTrack();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/tracks/${id}`, { withCredentials: true });
      toast.success("Track deleted");
      navigate("/admin/tracks");
    } catch {
      toast.error("Failed to delete");
      throw new Error("delete failed");
    }
  };

  const openPicker = async (type) => {
    setPickerType(type);
    setLibLoading(true);
    try {
      if (type === "lexp") {
        const { data } = await axios.get(`${API}/learning-experiences`, { withCredentials: true });
        setLibrary(data);
      } else if (type === "survey") {
        const { data } = await axios.get(`${API}/surveys?status=published`, { withCredentials: true });
        setLibrary(data);
      } else {
        const { data } = await axios.get(`${API}/assessment-types`, { withCredentials: true });
        setLibrary(data);
      }
    } catch { toast.error("Failed to load library"); }
    finally { setLibLoading(false); }
  };

  const handleAddItem = async (refId) => {
    try {
      await axios.post(`${API}/tracks/${id}/items`, { type: pickerType, ref_id: refId }, { withCredentials: true });
      const label = pickerType === "lexp" ? "LEXP" : pickerType === "survey" ? "Survey" : "Assessment";
      toast.success(`${label} added to track`);
      fetchTrack();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add"); }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await axios.delete(`${API}/tracks/${id}/items/${itemId}`, { withCredentials: true });
      toast.success("Removed from track");
      fetchTrack();
    } catch { toast.error("Failed to remove"); }
  };

  const handleMove = async (idx, direction) => {
    const items = [...(track?.items || [])];
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    [items[idx], items[targetIdx]] = [items[targetIdx], items[idx]];
    const newOrder = items.map((i) => i.item_id);
    try {
      await axios.put(`${API}/tracks/${id}/items/reorder`, { item_ids: newOrder }, { withCredentials: true });
      fetchTrack();
    } catch { toast.error("Failed to reorder"); }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
      </div>
    );
  }
  if (!track) return null;

  const assignedRefIds = new Set((track.items || []).map((i) => i.ref_id));
  const trackItems = track.items || [];

  return (
    <div className="space-y-6" data-testid="track-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/admin/tracks")} data-testid="track-back-btn"
        className="text-[#A1A9B8] hover:text-white hover:bg-white/5 w-fit">
        <ArrowLeft className="h-4 w-4 mr-1.5" />Back to Tracks
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-white tracking-tight break-words min-w-0" data-testid="track-detail-title">{track.title}</h1>
          <Badge className={track.status === "published" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" : "bg-amber-500/15 text-amber-400 border-amber-500/20"}>
            {track.status}
          </Badge>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setDeleteOpen(true)} data-testid="track-delete-btn" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 w-fit">
          <Trash2 className="h-4 w-4 mr-1.5" />Delete Track
        </Button>
      </div>

      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        tier={2}
        entityName={track?.title}
        entityType="track"
        entityId={id}
        onConfirm={handleDelete}
      />

      {/* Edit Form */}
      <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-6 space-y-5">
        <div className="space-y-1.5">
          <Label className="text-[#A1A9B8] font-body text-sm">Title <span className="text-red-400">*</span></Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="track-edit-title" className="bg-[#0A0C10] border-white/10 text-white h-10" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#A1A9B8] font-body text-sm">Description</Label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="track-edit-description" className="bg-[#0A0C10] border-white/10 text-white resize-none" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#A1A9B8] font-body text-sm">Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger className="bg-[#0A0C10] border-white/10 text-white h-10 max-w-xs" data-testid="track-edit-status"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#11141A] border-white/10">
              <SelectItem value="draft" className="text-white font-body">Draft</SelectItem>
              <SelectItem value="published" className="text-white font-body">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Submission Settings */}
        <div className="border-t border-white/10 pt-5 space-y-4">
          <h3 className="font-body font-bold text-sm text-white">Submission Settings</h3>
          <div className="space-y-1.5">
            <Label className="text-[#A1A9B8] font-body text-sm">Submission Type</Label>
            <div className="flex gap-2">
              {[{ v: "generic", l: "Generic" }, { v: "custom", l: "Custom" }].map((t) => (
                <button key={t.v} type="button" onClick={() => setForm({ ...form, submission_type: t.v })} data-testid={`submission-type-${t.v}`}
                  className={`px-4 py-2 text-sm font-body rounded-lg border transition-colors ${form.submission_type === t.v ? "border-white/20 bg-white/10 text-white" : "border-white/10 text-[#6E7787] hover:text-white hover:border-white/15"}`}>
                  {t.l}
                </button>
              ))}
            </div>
          </div>
          {form.submission_type === "custom" && (
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Custom Submission Message</Label>
              <Textarea value={form.custom_submission_message} onChange={(e) => setForm({ ...form, custom_submission_message: e.target.value })} rows={4}
                data-testid="custom-submission-message" className="bg-[#0A0C10] border-white/10 text-white resize-none" placeholder="Write the message learners will see..." />
            </div>
          )}
        </div>

        <Button onClick={handleSave} disabled={saving || !form.title.trim()} data-testid="track-save-btn"
          className="h-10 px-5 font-body font-medium rounded-lg hover:opacity-90"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}Save Changes
        </Button>
      </div>

      {/* Track Items */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-body font-bold text-lg text-white">Track Items</h2>
          <div className="flex gap-2">
            <Button onClick={() => openPicker("lexp")} data-testid="add-lexp-btn"
              className="h-9 px-4 font-body text-sm font-medium rounded-lg hover:opacity-90"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}>
              <BookOpen className="h-4 w-4 mr-1.5" />Add LEXP
            </Button>
            <Button onClick={() => openPicker("survey")} data-testid="add-survey-btn"
              variant="outline"
              className="h-9 px-4 font-body text-sm font-medium rounded-lg border-white/15 text-white hover:bg-white/5">
              <MessageSquare className="h-4 w-4 mr-1.5" />Add Survey
            </Button>
          </div>
        </div>

        {trackItems.length === 0 ? (
          <div className="bg-[#11141A] border border-dashed border-white/10 rounded-xl p-12 text-center" data-testid="track-items-empty">
            <BookOpen className="h-10 w-10 text-[#6E7787] mx-auto mb-3" />
            <p className="text-[#6E7787] font-body">No items added yet. Add LEXPs or Surveys to build this track.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="track-items-list">
            {trackItems.map((item, idx) => (
              <div key={item.item_id}
                className="bg-[#11141A] border border-white/10 rounded-lg px-4 py-3 flex items-center gap-3 hover:border-white/20 transition-colors"
                data-testid={`track-item-${item.item_id}`}>
                <div className="flex flex-col items-center gap-0.5 shrink-0">
                  <button onClick={() => handleMove(idx, "up")} disabled={idx === 0} className="text-[#6E7787] hover:text-white disabled:opacity-30 p-0.5" data-testid={`item-up-${item.item_id}`}>
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <span className="text-xs text-[#6E7787] font-body w-5 text-center">{idx + 1}</span>
                  <button onClick={() => handleMove(idx, "down")} disabled={idx === trackItems.length - 1} className="text-[#6E7787] hover:text-white disabled:opacity-30 p-0.5" data-testid={`item-down-${item.item_id}`}>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>
                <div className="shrink-0">
                  {item.type === "lexp" ? (
                    <BookOpen className="h-5 w-5" style={{ color: "var(--primary-color, #4A90D9)" }} />
                  ) : item.type === "survey" ? (
                    <MessageSquare className="h-5 w-5 text-indigo-400" />
                  ) : (
                    <ClipboardList className="h-5 w-5 text-violet-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-body font-medium text-white truncate">{item.title}</h4>
                  <p className="text-xs text-[#6E7787] font-body mt-0.5">
                    {item.type === "lexp" ? "Learning Experience" : item.type === "survey" ? "Survey" : "Assessment"}
                    {item.item_count != null ? ` \u00B7 ${item.item_count} items` : ""}
                  </p>
                </div>
                <Badge className={`shrink-0 ${item.type === "lexp"
                  ? (item.status === "published" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" : "bg-amber-500/15 text-amber-400 border-amber-500/20")
                  : item.type === "survey"
                  ? "bg-indigo-500/15 text-indigo-400 border-indigo-500/20"
                  : "bg-violet-500/15 text-violet-400 border-violet-500/20"}`}>
                  {item.type === "lexp" ? item.status : item.type === "survey" ? "Survey" : "Assessment"}
                </Badge>
                <Button variant="ghost" size="sm" onClick={() => handleRemoveItem(item.item_id)}
                  data-testid={`item-remove-${item.item_id}`}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs h-7 px-2 shrink-0">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Item Picker */}
      <Dialog open={!!pickerType} onOpenChange={() => setPickerType(null)}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto" data-testid="item-picker-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-xl text-white">
              {pickerType === "lexp" ? "Add Learning Experience" : pickerType === "survey" ? "Add Survey" : "Add Assessment"}
            </DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body">
              {pickerType === "lexp"
                ? "Select a learning experience to add to this track."
                : pickerType === "survey"
                ? "Select a published survey. Placement in the track sequence works like any other item — reorder after adding."
                : "Select an assessment from the Assessment Library."}
            </DialogDescription>
          </DialogHeader>
          {libLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
            </div>
          ) : library.length === 0 ? (
            <p className="text-[#6E7787] font-body text-sm py-6 text-center">
              {pickerType === "lexp"
                ? "No learning experiences available."
                : pickerType === "survey"
                ? "No published surveys available. Publish a survey in the Survey Library first."
                : "No assessments available."}
            </p>
          ) : (
            <div className="space-y-2 mt-2">
              {library.map((le) => {
                const refId = le.id;
                const alreadyAdded = assignedRefIds.has(refId);
                return (
                  <div key={refId}
                    className={`border border-white/10 rounded-lg p-3 transition-all ${alreadyAdded ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-white/20"}`}
                    onClick={() => !alreadyAdded && handleAddItem(refId)}
                    data-testid={`picker-item-${refId}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {pickerType === "lexp" ? (
                          <BookOpen className="h-4 w-4 shrink-0" style={{ color: "var(--primary-color, #4A90D9)" }} />
                        ) : pickerType === "survey" ? (
                          <MessageSquare className="h-4 w-4 text-indigo-400 shrink-0" />
                        ) : (
                          <ClipboardList className="h-4 w-4 text-violet-400 shrink-0" />
                        )}
                        <div>
                          <h4 className="text-sm font-body font-medium text-white">{le.title || le.name}</h4>
                          {le.description && <p className="text-xs text-[#6E7787] font-body mt-0.5 line-clamp-1">{le.description}</p>}
                          {pickerType === "assessment" && <p className="text-xs text-[#6E7787] font-body mt-0.5">{le.source === "native" ? "Native" : "External"}</p>}
                          {pickerType === "survey" && le.item_count != null && <p className="text-xs text-[#6E7787] font-body mt-0.5">{le.item_count} question{le.item_count === 1 ? "" : "s"}{le.anonymous ? " · Anonymous" : ""}</p>}
                        </div>
                      </div>
                      {alreadyAdded ? (
                        <Badge className="bg-white/5 text-[#6E7787] border-white/10 shrink-0">Added</Badge>
                      ) : (
                        <Plus className="h-4 w-4 text-[#6E7787] shrink-0 mt-0.5" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
